import React, { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { FiBarChart2, FiUsers, FiCreditCard } from 'react-icons/fi';
import type { IconBaseProps } from 'react-icons';
import { isMobileDevice } from '../utils/deviceDetection';
import { colors, spacing, borderRadius, animations, zIndex } from '../styles/designTokens';

// 모바일 레이아웃 컨테이너
const MobileLayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(135deg, ${colors.semantic.background.secondary} 0%, ${colors.semantic.background.tertiary} 100%);
  position: relative;
  
  /* 배경 패턴 */
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.03) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }
  
  /* 안전 영역 고려 (iPhone X 이상) */
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  
  /* 가로 모드 최적화 */
  @media (max-height: 500px) and (orientation: landscape) {
    min-height: 100vh;
  }
`;


// 모바일 메인 컨텐츠 - 스크롤 최적화 (헤더 제거로 상단 패딩 조정)
const MobileMainContent = styled.main`
  flex: 1;
  overflow-y: auto;
  padding: ${spacing[4]} ${spacing[4]};
  padding-top: max(${spacing[4]}, env(safe-area-inset-top)); /* 안전 영역 고려 */
  padding-bottom: 120px; /* 하단 네비게이션 바 공간 확보 */
  position: relative;
  z-index: 1;
  
  /* 스크롤 최적화 */
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  
  /* 매우 작은 모바일 (480px 이하) */
  @media (max-width: 480px) {
    padding: ${spacing[3]} ${spacing[3]};
    padding-top: max(${spacing[3]}, env(safe-area-inset-top));
    padding-bottom: 110px;
  }
  
  /* 가로 모드 최적화 */
  @media (max-height: 500px) and (orientation: landscape) {
    padding-top: max(${spacing[2]}, env(safe-area-inset-top));
    padding-bottom: 100px;
  }
`;

// 하단 네비게이션 바 - 현대적인 모바일 UX
const BottomNavigation = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 88px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(24px);
  border-top: 1px solid ${colors.semantic.border.primary};
  display: flex;
  justify-content: space-around;
  align-items: flex-start;
  z-index: ${zIndex.layoutBottomNav};
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.12);
  padding: ${spacing[3]} ${spacing[2]} ${spacing[2]};
  
  /* 작은 모바일 최적화 */
  @media (max-width: 480px) {
    height: 80px;
    padding: ${spacing[2]} ${spacing[1]} ${spacing[1]};
  }
  
  /* 가로 모드 최적화 */
  @media (max-height: 500px) and (orientation: landscape) {
    height: 72px;
    padding: ${spacing[2]} ${spacing[1]} ${spacing[1]};
  }
  
  /* 안전 영역 고려 (iPhone X 이상) */
  padding-bottom: max(${spacing[2]}, env(safe-area-inset-bottom));
`;

// 하단 네비게이션 아이템 - 터치 최적화
const BottomNavItem = styled.button<{ isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${spacing[2]} ${spacing[1]};
  border-radius: ${borderRadius.xl};
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  min-width: 64px;
  min-height: 56px;
  position: relative;
  overflow: hidden;
  
  /* 터치 영역 확보 */
  &::after {
    content: '';
    position: absolute;
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    z-index: 0;
  }
  
  /* 작은 모바일 최적화 */
  @media (max-width: 480px) {
    min-width: 56px;
    min-height: 52px;
    padding: ${spacing[1]} ${spacing[1]};
  }
  
  /* 가로 모드 최적화 */
  @media (max-height: 500px) and (orientation: landscape) {
    min-width: 52px;
    min-height: 48px;
    padding: ${spacing[1]};
  }
  
  .icon {
    color: ${props => props.isActive ? colors.primary[600] : colors.semantic.text.tertiary};
    margin-bottom: 4px;
    transition: all ${animations.duration.normal} ${animations.easing.smooth};
    position: relative;
    z-index: 2;
    filter: ${props => props.isActive ? 'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3))' : 'none'};
    
    /* 작은 모바일 최적화 */
    @media (max-width: 480px) {
      margin-bottom: 3px;
    }
    
    /* 가로 모드 최적화 */
    @media (max-height: 500px) and (orientation: landscape) {
      margin-bottom: 2px;
    }
  }
  
  .label {
    font-size: 11px;
    font-weight: ${props => props.isActive ? 700 : 500};
    color: ${props => props.isActive ? colors.primary[600] : colors.semantic.text.tertiary};
    text-align: center;
    line-height: 1.2;
    transition: all ${animations.duration.normal} ${animations.easing.smooth};
    position: relative;
    z-index: 2;
    letter-spacing: 0.3px;
    
    /* 작은 모바일 최적화 */
    @media (max-width: 480px) {
      font-size: 10px;
    }
    
    /* 가로 모드 최적화 */
    @media (max-height: 500px) and (orientation: landscape) {
      font-size: 9px;
    }
  }
  
  /* 액티브 상태 - 현대적인 디자인 */
  ${props => props.isActive && css`
    background: linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.primary[100]} 100%);
    transform: translateY(-2px);
    
    &::before {
      content: '';
      position: absolute;
      top: 6px;
      left: 50%;
      transform: translateX(-50%);
      width: 6px;
      height: 6px;
      background: ${colors.primary[600]};
      border-radius: 50%;
      z-index: 1;
      box-shadow: 0 0 8px rgba(59, 130, 246, 0.4);
    }
    
    .icon {
      transform: scale(1.1);
    }
    
    .label {
      transform: translateY(-1px);
    }
  `}
  
  /* 터치 피드백 - 햅틱 피드백 시뮬레이션 */
  &:active {
    transform: ${props => props.isActive ? 'translateY(-1px) scale(0.95)' : 'scale(0.95)'};
    background: ${colors.semantic.background.secondary};
    
    .icon {
      transform: scale(0.9);
    }
    
    .label {
      transform: translateY(0);
    }
  }
  
  /* 접근성 - 포커스 상태 */
  &:focus-visible {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
  }
  
  /* 모션 감소 */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
    
    &:active {
      transform: none !important;
    }
  }
`;

// 네비게이션 아이템 타입 정의
interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<IconBaseProps>;
}

// 모바일 메인 레이아웃 컴포넌트
const MainLayoutMobile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // 데스크탑 디바이스인 경우 데스크탑 대시보드로 강제 이동
  useEffect(() => {
    // isMobileDevice()가 false면 데스크탑으로 간주
    if (!isMobileDevice()) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  // 네비게이션 핸들러
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // 현재 경로 확인 함수
  const isActivePath = (path: string): boolean => {
    return location.pathname.startsWith(path);
  };

  // 네비게이션 메뉴 아이템들
  const navItems: NavItem[] = [
    { path: '/mobile/dashboard', label: '대시보드', icon: FiBarChart2 },
    { path: '/mobile/customers', label: '고객', icon: FiUsers },
    { path: '/mobile/payments', label: '결제', icon: FiCreditCard },
    // { path: '/mobile/keywords', label: '키워드', icon: FiSearch },
    // { path: '/mobile/products', label: '상품', icon: FiPackage },
  ];

  return (
    <MobileLayoutContainer>
      {/* 모바일 메인 컨텐츠 */}
      <MobileMainContent>
        <Outlet />
      </MobileMainContent>

      {/* 하단 네비게이션 바 */}
      <BottomNavigation>
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <BottomNavItem
              key={item.path}
              isActive={isActivePath(item.path)}
              onClick={() => handleNavigation(item.path)}
            >
              <div className="icon">
                <Icon size={22} />
              </div>
              <div className="label">{item.label}</div>
            </BottomNavItem>
          );
        })}
      </BottomNavigation>
    </MobileLayoutContainer>
  );
};

export default MainLayoutMobile; 